import * as moment from 'moment';
import { TimeSpan } from './../../shared/models/timespan';

// TODO: improve documentation

/**
 * Gets current utc date
 * @returns ISO6801 string
 */
export function getCurrentUtcDate(): string{
  return moment().toISOString();
}

/**
 * @returns ISO6801 string
 */
export function getCurrentLocaleDate(): string {
   return moment().toISOString(true);
}

/**
 * Takes local date and coverts into IS8601 string
 * @returns ISO6801 string
 * @param date Local date
 */
export function getUtcDate(date: string | Date): string{
  return moment(date).toISOString();
}

/**
 * Takes local date and coverts into ISO8601 string
 * @returns ISO6801 string
 * @param date Local date
 */
export function getUtcAuDate(date: string | Date): string{
  return moment(date).toISOString(true);
}

/**
 *
 * @param date Date to work with
 * @param reverseOffset Local Date if true. If this is null and so is offset, it returns same date.
 * @param offset UTC offset if true. If this is null and so is offset, it returns same date.
 *
 * @returns ISO formatted string
 */
export function getIsoDate(date: string | Date, reverseOffset?: boolean, offset?: boolean): string {
  let isoDate = '';

  if (reverseOffset) {
    isoDate = moment(new Date(date)).local().toISOString();
  }
  else if (offset && (reverseOffset === null || reverseOffset === false)) {
    isoDate = moment(new Date(date)).add(moment(date).utcOffset(), 'minutes').toISOString();
  }

  else {
    isoDate =  moment(new Date(date)).toISOString();
  }
  return isoDate;
}

/**
 * Returns date format that is used by the Calendar
 * @returns YYYY-MM-DD HH:mm:ss formatted string
 * @param date UTC Date
 */
export function getCalendarDate(date: string | Date): string {
  const offsetMins = moment(date).utcOffset();
  let calendarDate = moment(date).subtract(offsetMins, 'minutes')
  .format('YYYY-MM-DD HH:mm:ss').replace(/T.*$/, '');
  return calendarDate;
}

/**
 * Returns date format that is used by the Calendar.
 * This also adds number of hours to make sure the calendar date highlight works
 * @param date UTC Date
 * @param hoursToAdd Number of hours to add
 * @returns YYYY-MM-DD HH:mm:ss formatted string
 */
export function getCalendarDate2(date: string | Date, hoursToAdd: number): string {
  const offsetMins = moment(date).utcOffset();
  let calendarDate = moment(date).subtract(offsetMins, 'minutes')
  .add(hoursToAdd, 'hours')
  .format('YYYY-MM-DD HH:mm:ss').replace(/T.*$/, '');
  return calendarDate;
}

// TODO: Use this where the application is using moment directly

/**
 * Adds days to a date
 * @param date The date
 * @param days Number of days to add
 * @returns string
 */
export function addDays(date: string | Date, days: number): string{
  return moment(date).add(days, 'days').toString();
}

/**
 * Return absolute value of difference in two dates
 * @param date1
 * @param date2
 * @returns Number
 */
export function getDaysInBetween(date1: string | Date, date2: string | Date): number {
  let daysInBetween =  moment(date1).diff(moment(date2), 'days');
  return Math.abs(daysInBetween);
}

export function getCalendarDateWithoutOffset(date: string | Date) {
  return moment(date).format('YYYY-MM-DD HH:mm:ss').replace(/T.*$/, '');
}

/**
 * Returns date component from DateTime
 * @param date
 */
export function getDate(date: string | Date) {
  return moment(date).format('YYYY-MM-DD');
}

export function isDateInRange(startDate: Date | string, endDate: Date | string, date: Date | string) {
   return moment(date).isBetween(moment(startDate), moment(endDate), 'days', '[]');
}

export function isDateInRange2(startDate: Date | string, endDate: Date | string, date: Date | string) {
  return moment(date).isBetween(moment(startDate), moment(endDate), 'minutes', '[)'); // note that end date is not inclusive
}

/**
 * This only compares the time element by setting the date to today's date
 * @param startDateOrTime datetime or time with time element in HH:mm:ss format
 * @param endDateOrTime datetime or time with time element in HH:mm:ss format
 * @param dateOrTime datetime or time with time element in hh:mm:ss A format
 * @returns true if dateOrTime is between startDateOrTime (inclusive) and before endDateOrTime (not inclusive)
 */
export function isTimeInRange(startDateOrTime: string, endDateOrTime: string, dateOrTime: string): boolean {
  // seems to work for 22:00:00 times and 2:00 PM
  const format = 'HH:mm:ss';
  const format2 = 'hh:mm:ss A';
  const startTime = moment(startDateOrTime, format);
  const endTime = moment(endDateOrTime, format);
  const timeToCompare = moment(dateOrTime, format2);

  return timeToCompare.isBetween(startTime, endTime, 'minutes', '[)');
}

/**
 * This only compares the time element by setting the date to today's date
 * @param startDate datetime in YYYY-MM-DD HH:mm:ss format
 * @param endDate datetime in YYYY-MM-DD hh:mm:ss A format
 * @param date datetime in YYYY-MM-DD hh:mm:ss A format
 * @returns true if dateOrTime is between startDateOrTime (inclusive) and before endDateOrTime (not inclusive)
 */
export function isDateTimeInRange(startDate: string | Date, endDate: string | Date, dateOrTime: string): boolean {
  // seems to work for 22:00:00 times and 2:00 PM
  const format = 'YYYY-MM-DD hh:mm:ss A';
  const format2 = 'YYYY-MM-DD HH:mm:ss';
  const startTime = moment(startDate, format2);
  const endTime = moment(endDate, format2);
  const dateToCompare = moment(dateOrTime, format);

  return dateToCompare.isBetween(startTime, endTime, 'minutes', '[)');
}

/**
 * This tests if a date is between two dates
 * @param startDate datetime as string
 * @param endDate datetime as string
 * @param date datetime as string
 * @returns true if date is after startDateOrTime (exclusive) and within endDate (inclusive)
 */
export function isTimeInRange4(startDate: string, endDate: string, date: string): boolean {
  // seems to work for 2020-07-16 02:00:00 times and 2020-07-16 2:00 PM
  const format = 'YYYY-MM-DD hh:mm:ss A';
  const start = moment(startDate, format);
  const end = moment(endDate, format);
  const dateToCompare = moment(date, format);

  return dateToCompare.isBetween(start, end, 'minutes', '(]');
}

/**
 * This only compares the time element by setting the date to today's date
 * @param startDateOrTime datetime or time with time element in HH:mm:ss format
 * @param endDateOrTime datetime or time with time element in HH:mm:ss format
 * @param dateOrTime datetime or time with time element in hh:mm:ss A format
 * @returns true if dateOrTime is after startDateOrTime (exclusive) and within endDateOrTime (inclusive)
 */
export function isTimeInRange3(startDateOrTime: string, endDateOrTime: string, dateOrTime: string): boolean {
 // seems to work for 22:00:00 times and 2:00 PM
 const format = 'HH:mm:ss ';
 const format2 = 'hh:mm:ss A';
 const start = moment(startDateOrTime, format);
 const end = moment(endDateOrTime, format);
 const dateToCompare = moment(dateOrTime, format2);

 return dateToCompare.isBetween(start, end, 'minutes', '(]');
}

/**
 * Gets Minutes from a TimeSpan object
 * @param timespan TimeSpan object
 */
export function getMinutesFromTimespan(timespan: TimeSpan): number {
  const format = 'hh:mm:ss';
  return moment(timespan, format).hours() * 60 + moment(timespan, format).minutes();
}

/**
 * Return true if two dates are same
 * @param date1
 * @param date2
 */
export function isSameDate(date1: string | Date, date2: string | Date): boolean {
  return moment(date1).isSame(moment(date2));
}

/**
 * Return true if two dates are same where dates are of format YYYY-MM-DD hh:mm A
 * @param date1
 * @param date2
 */
export function isSameDate2(date1: string | Date, date2: string | Date): boolean {
  const format = 'YYYY-MM-DD hh:mm A';
  return moment(date1, format).isSame(moment(date2, format), 'minutes');
}

/**
 * Returns true if date1 is earlier than date2
 * @param date1
 * @param date2
 */
export function isPastDate(date1: string | Date, date2: string | Date) {
  return moment(date1).isBefore(moment(date2));
}

/**
 * Returns true if date1 is earlier than date2 where dates are of format YYYY-MM-DD hh:mm A
 * @param date1
 * @param date2
 */
export function isPastDate2(date1: string | Date, date2: string | Date) {
  const format = 'YYYY-MM-DD hh:mm A';
  return moment(date1, format).isBefore(moment(date2, format), 'minutes');
}

/**
 * Returns Time (no seconds) for date
 * @param date
 */
export function getTimeFromData(date: string | Date) {
  return moment(date).format('hh:mm A').replace(' AM', 'a').replace(' PM', 'p');
}

/**
 * Returns the same date formatted in ISO 6801
 * @param date Date that is passed in
 */
export function getAuDate(date: string | Date){
  return moment(new Date(date)).format('DD/MM/YYYY HH:mm:ss');
}

export function getUsDate(date: string | Date) {
  return moment(new Date(date)).format('MM/DD/YYYY HH:mm:ss');
}
/**
 * Takes UTC date and returns the Browser Timezone adjusted date.
 * This takes care of daylight saving
 * @param utcDate UTC Date
 */
export function getLocaleOffsetDate(utcDate: string | Date) {
  return getUtcOffset(utcDate).format('MM/DD/YYYY HH:mm:ss');
}

function getUtcOffset(date) {
  return moment(date)
    .add(moment(date).utcOffset(), 'minutes');
}





